import { useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Card,
  Checkbox,
  Badge,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { AlertCircle, File, Link as LinkIcon } from 'react-feather';
import type { ResourceCategory, ResourceWithDetails } from '../../../types';
import { resourcesApi } from '../../../services/api/resources';

interface EditEvergreenResourceModalProps {
  opened: boolean;
  onClose: () => void;
  resource: ResourceWithDetails;
  categories: ResourceCategory[];
  onUpdated: () => void;
}

export function EditEvergreenResourceModal({
  opened,
  onClose,
  resource,
  categories,
  onUpdated,
}: EditEvergreenResourceModalProps) {
  const form = useForm({
    initialValues: {
      title: resource.title,
      description: resource.description || '',
      category_id: resource.category_id || '',
      access_coaches: resource.access_coaches,
      access_participants: resource.access_participants,
      access_partners: resource.access_partners,
    },
    validate: {
      title: (value) => (value.trim() ? null : 'Title is required'),
    },
  });

  // Reset form when resource changes
  useEffect(() => {
    form.setValues({
      title: resource.title,
      description: resource.description || '',
      category_id: resource.category_id || '',
      access_coaches: resource.access_coaches,
      access_participants: resource.access_participants,
      access_partners: resource.access_partners,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: typeof form.values) => {
    try {
      await resourcesApi.update(resource.id, {
        title: values.title,
        description: values.description || undefined,
        category_id: values.category_id || undefined,
        access_coaches: values.access_coaches,
        access_participants: values.access_participants,
        access_partners: values.access_partners,
      });

      onUpdated();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update resource',
        color: 'red',
      });
    }
  };

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Evergreen Resource"
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Resource type indicator */}
          <Card withBorder p="sm" bg="gray.0">
            <Group>
              {resource.resource_type === 'link' ? (
                <LinkIcon size={20} />
              ) : (
                <File size={20} />
              )}
              <div>
                <Text size="sm" fw={500}>
                  {resource.resource_type === 'link' ? 'Link Resource' : 'File Resource'}
                </Text>
                {resource.resource_type === 'link' && resource.url && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {resource.url}
                  </Text>
                )}
                {resource.resource_type === 'file' && (
                  <Text size="xs" c="dimmed">
                    {resource.original_filename}
                  </Text>
                )}
              </div>
              <Badge size="sm" color="teal" variant="light" ml="auto">
                EVERGREEN
              </Badge>
            </Group>
          </Card>

          {/* Editable fields */}
          <TextInput
            label="Title"
            placeholder="Resource title"
            required
            {...form.getInputProps('title')}
          />

          <Textarea
            label="Description"
            placeholder="Optional description"
            autosize
            minRows={2}
            {...form.getInputProps('description')}
          />

          <Select
            label="Category"
            placeholder="Select category"
            data={categoryOptions}
            clearable
            {...form.getInputProps('category_id')}
          />

          {/* Access permissions */}
          <Card withBorder p="sm">
            <Text size="sm" fw={500} mb="sm">
              Access Permissions
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              Select which user groups can view this resource across all cohorts
            </Text>
            <Group>
              <Checkbox
                label="Coaches"
                {...form.getInputProps('access_coaches', { type: 'checkbox' })}
              />
              <Checkbox
                label="Participants"
                {...form.getInputProps('access_participants', { type: 'checkbox' })}
              />
              <Checkbox
                label="Partners"
                {...form.getInputProps('access_partners', { type: 'checkbox' })}
              />
            </Group>
          </Card>

          {/* At least one access must be selected */}
          {!form.values.access_coaches &&
            !form.values.access_participants &&
            !form.values.access_partners && (
              <Alert icon={<AlertCircle size={16} />} color="yellow">
                At least one access group should be selected
              </Alert>
            )}

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
